import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { CommonModule } from "@angular/common";

// Material MDC Imports
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSidenavModule } from "@angular/material/sidenav";
import { ViewerComponentsModule } from "../../../viewer-components/viewer-components.module";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MapContainerComponent } from "../../../viewer-components/components/containers/map-container/map-container.component";
import { IViewerconfiguration } from "../../../viewer-components/interfaces/i-viewerconfiguration";
import moment from "moment/moment";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { filter } from "rxjs/operators";
import { ViewerConfigurationService } from "../../../viewer-components/services/viewer-configuration.service";
import { SearchState } from "../../model";
import { SearchQueryService } from "../../services/search";
import { AgentDefinition } from "../../../ai-components/chat/model/agent.types";
import type { ToolRendererConfig } from "../../../ai-components/chat/model/tool-data.types";

/**
 * Map display component with sidebar for search results
 * Maintains the sidebar with scrollable search results and map content
 */
@Component({
  selector: "app-compound-map-display",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./map-display-search.component.html",
  styleUrls: ["./map-display-search.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    MatTooltipModule,
    // ViewerComponentsModule,
  ],
})
export class MapDisplaySearchComponent implements OnInit, OnDestroy {
  private searchQueryService = inject(SearchQueryService);

  private viewerConfigurationService = inject(ViewerConfigurationService);

  private destroyRef = inject(DestroyRef);

  private changeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild("mapContainer", { read: ViewContainerRef, static: true })
  container: ViewContainerRef;

  componentRef: ComponentRef<MapContainerComponent>;

  /** Current search state */
  searchState: SearchState | null = null;

  /** AI agents forwarded to all map containers. */
  @Input() aiAgents: AgentDefinition[] = [];

  /** Tool renderers forwarded to all map containers. */
  @Input() aiToolRenderers: ToolRendererConfig[] = [];

  ngOnInit(): void {
    this.subscribeToSearchState();
    this.subscribeToConfiguration();
  }

  ngOnDestroy(): void {
    this.componentRef.destroy();
    this.container.clear();
  }

  /**
   * Remove all views in container and add one
   */
  initializeComponent(viewerConf: IViewerconfiguration) {
    this.container.clear();
    this.addMapComponent(viewerConf);
    this.changeDetectorRef.detectChanges();
  }

  addMapComponent(viewerConf: IViewerconfiguration = null): void {
    // Create component directly (no factory needed)
    this.componentRef = this.container.createComponent(MapContainerComponent);

    // Set additional properties as before
    const limitDay = moment().add(ViewerConstants.WARNING_EXPIRE_DAYS, "days");
    this.componentRef.instance.warningExpireUser = viewerConf?.userExpireDate
      ? limitDay.diff(moment(viewerConf.userExpireDate)) >= 0
      : false;

    this.componentRef.instance.aiAgents = this.aiAgents;
    this.componentRef.instance.aiToolRenderers = this.aiToolRenderers;
  }

  /**
   * Subscribes to search state changes from the service
   */
  private subscribeToSearchState(): void {
    this.searchQueryService.searchState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      this.searchState = state;
      this.changeDetectorRef.detectChanges();
    });
  }

  /**
   * Subscribes to viewer configuration data
   */
  private subscribeToConfiguration(): void {
    this.viewerConfigurationService.viewerConfiguration$
      .pipe(
        filter((state) => state !== null),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => {
        this.initializeComponent(state);
      });
  }
}
